/**
 * LpFlowProgress
 * 「ご契約の流れ」の縦線を進捗バーにして、**スクロール量に比例して**黄を伸ばす。
 *
 * @usage
 * <div class="p-lp-flow-list js-lp-flow">
 *   <div class="p-lp-flow-step js-lp-flow-step">
 *     <span class="num">01</span>
 *     …
 *
 * 段階的に点けるのではなく連続値。**丸の中央あたりで黄が止まる**ような、
 * 中途半端な位置で止まるのが正しい状態（ディレクター指示）。
 * 丸は黄が自分の中心に届いた時点で点く＝線に追い越された順に色が変わる。
 * 上へ戻れば黄も縮み、丸も戻る。
 *
 * デザインの静止画は「01〜03が黄・04〜06がグレー」だが、これは03まで進んだ瞬間の
 * スナップショットであって初期状態ではない。初期は全部グレー。
 *
 * 初期状態に落とすのは `<html>` に `has-lp-flow` が付いてからなので、
 * **JSが動かなければ全部黄（完成状態）で出る**。LPの流儀（LpReveal＝全部表示／
 * LpToggleMore＝全件表示）に合わせている。
 *
 * **レール（丸と縦線）は `js-lp-reveal` の対象にしない。**ここが動くと
 * 進捗の基準がぶれる。ふわっと出すのはバッジと本文だけ（`_flow.njk`）。
 *
 * 線の全長と上端は ::before（土台のグレー）の値を読む。どちらもデザインの実測値で
 * CSS側にしか無く、しかもPCとSPで違う（SPは先頭がバッジの帯なのでリストの先頭から）。
 * JSに同じ数値を持たせない。
 *
 * **`scroll` イベントでは追えない。**このLPは Lenis で慣性スクロールを掛けており、
 * **Lenisが動いている間 native の `scroll` は飛ばない**（実測：400px動いて0回）。
 * Lenisのインスタンスは `LpSmoothScroll` の中にあって外から触れないので、
 * **自前のrAFでスクロール量を見る**。回しっぱなしにしないよう、節が画面に近いあいだだけ
 * IntersectionObserver で回す。`prefers-reduced-motion` ではLenis自体が動かないが、
 * その場合もこの作りで問題ない（そもそも全部点けて終わり）。
 */

// 画面のどこに来たら「そこまで進んだ」とみなすかの基準線（画面高に対する割合）。
// LpReveal が「下から15%入ったら出す」なので、それに合わせて 0.85 にしてある
const REFERENCE = 0.85;

// 監視を始める範囲。節が画面に近づいたらrAFを回し、離れたら止める
const ROOT_MARGIN = '20% 0px 20% 0px';

/** ある要素の、基準要素からの縦位置。transform の影響を受けない */
function offsetWithin(element, root) {
  let top = 0;
  let node = element;
  while (node && node !== root) {
    top += node.offsetTop;
    node = node.offsetParent;
  }
  return top;
}

export default class LpFlowProgress {
  /**
   * @param {HTMLElement} element - `.js-lp-flow`
   */
  constructor(element) {
    this.element = element;
    this.steps = Array.from(element.querySelectorAll('.js-lp-flow-step'));
    if (!this.steps.length) {
      return;
    }

    document.documentElement.classList.add('has-lp-flow');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.steps.forEach((step) => step.classList.add('is-active'));
      this.element.style.setProperty('--flow-progress', '100%');
      return;
    }

    // **読み込み中は測らない。**measure() は getComputedStyle・offsetTop・offsetHeight・
    // 矩形を読む＝そのたびに全長2万px超のページのレイアウトが強制される。
    // この節は下のほうにあり、画面に近づくまで値は要らない（実測523ms）。
    // 初期状態（全部グレー・進捗0）はCSSの既定なので、測らなくても正しく出ている
    this.frame = 0;
    this.onFrame = () => {
      this.update();
      this.frame = window.requestAnimationFrame(this.onFrame);
    };
    this.onResize = () => {
      if (!this.centers) {
        return;
      }
      this.measure();
      this.update();
    };
    window.addEventListener('resize', this.onResize, { passive: true });

    // この節より上にある17のアコーディオンが開くと位置がずれるので、
    // 文書の高さが変わったら測り直す。ただし**回っているあいだだけ**。
    // 読み込み中は画像やフォントで何度も高さが変わり、そのたびに測ると
    // レイアウトを強制して重くなる（実測178ms）。画面から遠いうちは測る必要もない。
    // **高さは entry から取る。**DOMから読み直すとそれ自体がレイアウトを強制する
    if (typeof ResizeObserver === 'function') {
      this.docObserver = new ResizeObserver(([entry]) => {
        const h = entry.contentRect.height;
        if (h === this.docHeight) {
          return;
        }
        this.docHeight = h;
        if (this.frame) {
          this.onResize();
        }
      });
      this.docObserver.observe(document.documentElement);
    }

    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.start();
        return;
      }
      this.stop();
      // 離れた先の状態（0か全長）にしてから止める。
      // 一度も近づいていなければ測っていないので、そのまま初期状態でよい
      if (this.centers) {
        this.update();
      }
    }, { rootMargin: ROOT_MARGIN });
    this.observer.observe(this.element);
  }

  start() {
    if (!this.frame) {
      // 止まっているあいだに上の内容が伸び縮みしている可能性があるので測り直す
      this.measure();
      this.frame = window.requestAnimationFrame(this.onFrame);
    }
  }

  stop() {
    if (this.frame) {
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
  }

  /** 線の範囲と各丸の中心を、線の上端からの距離で持っておく */
  measure() {
    this.docTop = this.element.getBoundingClientRect().top + window.scrollY;
    const track = window.getComputedStyle(this.element, '::before');
    this.trackTop = parseFloat(track.top);
    this.trackHeight = parseFloat(track.height);
    this.centers = this.steps.map((step) => {
      const num = step.querySelector('.num');
      return offsetWithin(num, this.element) + num.offsetHeight / 2 - this.trackTop;
    });
  }

  update() {
    // 基準線が線の上端から何px下にあるか。
    // **毎コマ getBoundingClientRect を読まない。**このLPは全長2万px超で、
    // 深い要素の矩形を読むと祖先までのレイアウトが強制される（Lighthouse実測290ms）。
    // 位置は measure() で一度だけ取り、以後はスクロール量との差で出す
    const top = this.docTop - window.scrollY + this.trackTop;
    const height = Math.min(Math.max(window.innerHeight * REFERENCE - top, 0), this.trackHeight);

    if (height !== this.height) {
      this.height = height;
      this.element.style.setProperty('--flow-progress', `${height}px`);
    }
    // 丸01の中心は線の上端そのものなので、`>=` だと伸び始める前から点いてしまう
    this.steps.forEach((step, index) => {
      step.classList.toggle('is-active', height > this.centers[index]);
    });
  }

  destroy() {
    this.stop();
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.docObserver) {
      this.docObserver.disconnect();
    }
    if (this.onResize) {
      window.removeEventListener('resize', this.onResize);
    }
  }
}
